export type StorageEstimateInfo = {
  supported: boolean;
  persistSupported: boolean;
  persisted: boolean;
  usageBytes: number | null;
  quotaBytes: number | null;
};

const UNSUPPORTED: StorageEstimateInfo = {
  supported: false,
  persistSupported: false,
  persisted: false,
  usageBytes: null,
  quotaBytes: null,
};

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

export const formatBytes = (bytes: number | null): string => {
  if (bytes === null) {
    return 'unknown';
  }

  const exponent = Math.min(
    bytes > 0 ? Math.floor(Math.log(bytes) / Math.log(1024)) : 0,
    UNITS.length - 1
  );
  const value = bytes / 1024 ** exponent;

  return exponent === 0
    ? `${bytes} ${UNITS[0]}`
    : `${value.toFixed(1)} ${UNITS[exponent]}`;
};

const readPersisted = (storage: StorageManager) =>
  typeof storage.persisted === 'function'
    ? storage.persisted().catch(() => false)
    : Promise.resolve(false);

export const readStorageEstimate = async (): Promise<StorageEstimateInfo> => {
  const storage = navigator.storage;

  if (typeof storage?.estimate !== 'function') {
    return UNSUPPORTED;
  }

  try {
    const [estimate, persisted] = await Promise.all([
      storage.estimate(),
      readPersisted(storage),
    ]);

    return {
      supported: true,
      persistSupported: typeof storage.persist === 'function',
      persisted,
      usageBytes: estimate.usage ?? null,
      quotaBytes: estimate.quota ?? null,
    };
  } catch {
    return UNSUPPORTED;
  }
};

export const requestPersistentStorage = async (): Promise<boolean> => {
  const storage = navigator.storage;

  if (typeof storage?.persist !== 'function') {
    return false;
  }

  return storage.persist().catch(() => false);
};
